import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { debounce } from "@s-libs/micro-dash";
import { STANDARDS_SET_MODE } from "@/search/Langston/types/IStandardsSetMode";
import { useStandardsModalContext } from "@/search/Langston/FilterPanel/filters/Standards/StandardsModalProvider";

export const useStandardsModalTitleInput = () => {
  const {
    getters: {
      newStandardsSet: {
        id,
        title,
        children: standardsInNewSet,
        viewmodel_type,
      },
      createdStandardsSets,
      standardsSetMode,
      confirmModalData: { editData },
    },
    handlers: {
      setNewStandardsSet,
      setConfirmModalData,
      setStandardsSetTitleError,
    },
  } = useStandardsModalContext();

  const [inputTitle, setInputTitle] = useState<string>(title);
  const [standardsSetTitleTouched, setStandardsSetTitleTouched] =
    useState(false);

  const debouncedUpdateTitle = useMemo(
    () =>
      debounce((newTitle: string) => {
        if (standardsSetMode === STANDARDS_SET_MODE.CREATE) {
          setNewStandardsSet({
            id,
            title: newTitle,
            children: standardsInNewSet,
            viewmodel_type,
          });
        } else if (standardsSetMode === STANDARDS_SET_MODE.EDIT && editData) {
          setConfirmModalData({
            setId: editData.id,
            editData: {
              ...editData,
              title: newTitle,
            },
          });
        }

        setInputTitle(newTitle);
      }, 1000),
    [standardsSetMode, standardsInNewSet, editData, id],
  );

  const handleTitleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newTitle = e.target.value;
      setInputTitle(newTitle);
      debouncedUpdateTitle(newTitle);
    },
    [debouncedUpdateTitle],
  );

  const standardsInSet = useMemo(
    () =>
      standardsSetMode === STANDARDS_SET_MODE.CREATE
        ? standardsInNewSet
        : editData && standardsSetMode === STANDARDS_SET_MODE.EDIT
          ? editData.children
          : undefined,
    [standardsSetMode, editData, standardsInNewSet],
  );

  useEffect(() => {
    setStandardsSetTitleError(
      inputTitle === "" ? "Please enter a title." : null,
    );
    if (inputTitle === "" && standardsInSet && standardsInSet.length > 0) {
      setStandardsSetTitleTouched(true);
    }
  }, [title, standardsSetTitleTouched, standardsInSet?.length, inputTitle]);

  useEffect(() => {
    setStandardsSetTitleError(null);
    setStandardsSetTitleTouched(false);
  }, [createdStandardsSets.length]);

  useEffect(() => {
    setInputTitle(
      editData &&
        (standardsSetMode === STANDARDS_SET_MODE.EDIT ||
          standardsSetMode === STANDARDS_SET_MODE.DELETE)
        ? editData.title
        : standardsSetMode === STANDARDS_SET_MODE.CREATE
          ? title
          : "",
    );
  }, [standardsSetMode, editData, title]);

  return {
    inputTitle,
    handleTitleChange,
    standardsSetTitleTouched,
    setStandardsSetTitleTouched,
  };
};
